import type { Permission } from '@/types/auth';
import type { AssetRef, UpsellTargetPlan } from '../types';

/**
 * Per-asset-kind permission matrix for the Download Gateway.
 *
 * Capability gating runs on permissions
 * (`apps/uppbeat-frontend/CLAUDE.md` → "Auth: Permissions vs Roles" —
 * permissions are the source of truth for what the user can do). Roles
 * only inform tier-level UX — the free-user email-verify gate, the LUT
 * confirmation-skip, and the upsell-payload account-type projection.
 * None of those decide whether the user can download; they decide *how*
 * the flow renders.
 *
 * `premiumPermission` / `unlimitedPermission` accept either a single
 * permission or an OR-list. The OR form expresses "holding any of these
 * grants the gate" — used for LUTs, which legitimately accept either the
 * LUT-specific permission or the broader MG one because users with the
 * older permission scope shouldn't be re-upsold.
 */
export interface AssetRules {
  premiumPermission: Permission | readonly Permission[];
  unlimitedPermission: Permission | readonly Permission[];
  upsellTarget: UpsellTargetPlan;
}

type SupportedKind = AssetRef['kind'];

// Partial because future asset kinds may not have a rules row yet — when
// that happens, `getRules` returns undefined and `evaluateDownload` falls
// back to `pending` so the caller can route to a non-gateway flow.
const RULES: Partial<Record<SupportedKind, AssetRules>> = {
  music: {
    premiumPermission: 'download_premium_music',
    unlimitedPermission: 'unlimited_downloads_music',
    upsellTarget: 'Creator',
  },
  sfx: {
    premiumPermission: 'download_premium_sfx',
    unlimitedPermission: 'unlimited_downloads_sfx',
    upsellTarget: 'Creator',
  },
  mg: {
    premiumPermission: 'download_premium_mg',
    unlimitedPermission: 'unlimited_downloads_mg',
    upsellTarget: 'Creator',
  },
  lut: {
    // LUT-specific grants OR the broader MG grant — holding either unlocks
    // LUT downloads. Non-LUT motion assets do NOT get this fallback.
    premiumPermission: ['download_premium_lut', 'download_premium_mg'],
    unlimitedPermission: ['unlimited_downloads_lut', 'unlimited_downloads_mg'],
    upsellTarget: 'Creator',
  },
  'stock-video': {
    premiumPermission: 'download_premium_stock_video',
    unlimitedPermission: 'unlimited_downloads_stock_video',
    // Default target — covers the premium-asset and no-credits upsells. The
    // 4k resolution-locked upsell targets Pro instead and is handled inside
    // `evaluateStockVideo` (encoding it per-resolution here would invert
    // the rules table for one special case).
    upsellTarget: 'Creator',
  },
};

export const getRules = (kind: SupportedKind): AssetRules | undefined =>
  RULES[kind];

/**
 * Resolve a permission-or-OR-list against the user's permission set.
 * Single permission → membership check; array → "any of" check.
 */
export const holdsPermission = (
  permissions: ReadonlySet<Permission>,
  required: Permission | readonly Permission[]
): boolean =>
  Array.isArray(required)
    ? required.some((p) => permissions.has(p))
    : permissions.has(required as Permission);
